import React, { createContext, memo, useContext, useMemo } from 'react';

import classnames from 'classnames';

import { useSelectRootContext } from '../root/SelectRootContext';
import { useSelectRootFloatingContext } from '../root/SelectRootFloatingContext';

import { useSelectInput } from './useSelectInput';
import { useSelectTriggerProps } from './useSelectTriggerProps';

import styles from './SelectTrigger.module.scss';

export type SelectTriggerColor = 'primary' | 'error' | 'alert';

export type SelectTriggerProps = {
    className?: string;
    children?: React.ReactNode;
    customInput?: React.ReactElement;
    allowClear?: boolean;
    onClear?: () => void;
    showArrow?: boolean;
    color?: SelectTriggerColor;
} & Omit<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    'className' | 'children' | 'onChange' | 'onSelect'
>;

export const SelectTriggerContext = createContext<{
    customInput: React.ReactElement | undefined;
    hasCustomInput: boolean;
    allowClear: boolean;
    onClear: (() => void) | undefined;
    showArrow: boolean;
    color: SelectTriggerColor | undefined;
} | null>(null);

export const useSelectTriggerContext = () => {
    const context = useContext(SelectTriggerContext);
    if (!context) {
        throw new Error(
            'useSelectTriggerContext must be used within a SelectTriggerContext.Provider'
        );
    }
    return context;
};

export const SelectTrigger = memo(
    ({
        className,
        children,
        onBlur,
        onFocus,
        customInput: customInputProp,
        allowClear = false,
        onClear,
        showArrow = true,
        color,
        ...rest
    }: SelectTriggerProps) => {
        const { disabled, readOnly, autoComplete, size, showSearch, mode } = useSelectRootContext();
        const { floatingRefs } = useSelectRootFloatingContext();
        if (customInputProp && !autoComplete) {
            throw new Error('`customInput` can only be used in auto complete mode');
        }
        const customInput = autoComplete ? customInputProp : undefined;
        const hasCustomInput = Boolean(customInput);
        const selectorProps = useSelectTriggerProps(rest, { hasCustomInput });
        const selectInputNode = useSelectInput({ onBlur, onFocus, customInput });
        const contextValue = useMemo(
            () => ({ customInput, hasCustomInput, allowClear, onClear, showArrow, color }),
            [customInput, hasCustomInput, allowClear, onClear, showArrow, color]
        );
        return (
            <SelectTriggerContext.Provider value={contextValue}>
                <div
                    className={classnames(
                        styles.container,
                        {
                            [styles.single]: mode === 'single',
                            [styles.disabled]: disabled,
                            [styles.showSearch]: showSearch,
                            [styles.readOnly]: readOnly,
                            [styles.autoComplete]: autoComplete,
                            [styles.hasCustomInput]: hasCustomInput,
                            [styles.primary]: color === 'primary',
                            [styles.error]: color === 'error',
                            [styles.alert]: color === 'alert',
                            [styles.small]: size === 'small' && !hasCustomInput
                        },
                        className
                    )}
                    ref={floatingRefs.setPositionReference}
                    {...selectorProps}
                >
                    <div className={styles.inputContainer}>{selectInputNode}</div>
                    {children}
                </div>
            </SelectTriggerContext.Provider>
        );
    }
);

SelectTrigger.displayName = 'SelectTrigger';
